"use client";

import React, { PropsWithChildren, useEffect } from "react";
import { SessionProvider, signIn, signOut, useSession } from "next-auth/react";
import { Session } from "next-auth";

interface AuthProviderProps {
  children: React.ReactNode;
  session?: Session | null;
}

/**
 * Where the forced sign-out has to land the browser.
 *
 * `signOut` remembers nothing of the page it tears down, so a dead refresh
 * token on /dashboard/rep used to come back as a bare /auth — which has no
 * destination left to honour and falls through to the role-based module,
 * /admin for anyone who administers a workspace. The sign-in page itself is
 * never a destination, so it is the one URL left alone.
 */
const postSignOutUrl = () => {
  const authUrl = `${process.env.NEXT_PUBLIC_LANDING_URL}/auth`;
  const here = window.location.href;

  return new URL(here).pathname === "/auth"
    ? authUrl
    : `${authUrl}?redirect_to=${encodeURIComponent(here)}`;
};

function AuthGuard({ children }: PropsWithChildren) {
  const { data: session, status } = useSession();

  useEffect(() => {
    if (
      session?.error === "RefreshAccessTokenExpired" ||
      session?.error === "RefreshAccessTokenError"
    ) {
      if (status !== "authenticated") return;

      const handleLogout = async () => {
        await signOut({
          callbackUrl: postSignOutUrl(),
          redirect: true,
        });
      };

      handleLogout();
    }
  }, [session?.error]);

  if (status === "loading") {
    return null;
  }

  return children;
}

export function AuthProvider({ children, session }: AuthProviderProps) {
  return (
    <SessionProvider
      session={session}
      basePath={process.env.NEXT_PUBLIC_LANDING_URL + "/api/auth"}
      refetchOnWindowFocus={false}
    >
      <AuthGuard>{children}</AuthGuard>
    </SessionProvider>
  );
}
